import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, ShoppingCart, Check } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { cn, formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import BrandSymbol from '@/components/BrandSymbol';

interface AssociatedProduct {
  product_id: string;
  name: string;
  slug: string;
  price: number;
  image_url?: string;
}

export default function FrequentlyBoughtTogether({ productId, currentPrice }: { productId: string; currentPrice: number }) {
  const { t } = useTranslation('upsell');
  const { data: settingsData } = useSettings();
  const config = settingsData?.settings?.upsell_config?.frequently_bought_together;

  const { data } = useQuery({
    queryKey: ['associations', productId],
    queryFn: () =>
      api.get<{ associations: AssociatedProduct[] }>(`/associations/${productId}`),
    enabled: !!productId && !!config?.enabled,
  });

  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  if (!config?.enabled) return null;

  const associations = (data?.associations || []).slice(0, config.max_suggestions || 3);
  if (associations.length === 0) return null;

  // Initialize selection with all items
  if (selected.size === 0 && associations.length > 0) {
    const initial = new Set(associations.map((a) => a.product_id));
    // Use effect-free initialization
    if (initial.size > 0 && selected.size === 0) {
      setTimeout(() => setSelected(initial), 0);
    }
  }

  const toggleItem = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedItems = associations.filter((a) => selected.has(a.product_id));
  const combinedPrice = currentPrice + selectedItems.reduce((sum, i) => sum + Number(i.price), 0);
  const discountPercent = config.discount_percent || 0;
  const discountedTotal = discountPercent > 0 ? combinedPrice * (1 - discountPercent / 100) : combinedPrice;

  async function handleAddAll() {
    setIsAdding(true);
    try {
      for (const item of selectedItems) {
        await addItem(item.product_id);
      }
      addToast(t('frequentlyBoughtTogether.addedToast', { count: selectedItems.length }), 'success');
    } catch {
      addToast(t('frequentlyBoughtTogether.failedToast'), 'error');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <section className="mt-12 border border-line rounded-xl p-6 bg-surface-soft/50" aria-labelledby="fbt-heading">
      <h2 id="fbt-heading" className="text-lg font-bold text-ink mb-4">
        {config.title || t('frequentlyBoughtTogether.defaultTitle')}
      </h2>

      {/* Desktop: horizontal, Mobile: vertical stack */}
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start mb-6">
        {/* Current product */}
        <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:text-center">
          <div className="h-24 w-24 shrink-0 rounded-lg bg-sage-50 border-2 border-sage-600 flex items-center justify-center">
            <span className="text-xs text-sage-700 font-medium">{t('frequentlyBoughtTogether.thisItem')}</span>
          </div>
          <p className="text-sm font-medium text-ink-soft sm:max-w-[96px] sm:truncate">{t('frequentlyBoughtTogether.currentItem')}</p>
        </div>

        {associations.map((item) => {
          const isSelected = selected.has(item.product_id);
          return (
            <div key={item.product_id} className="flex items-center gap-3 sm:flex-col sm:items-center">
              <div className="flex items-center gap-2 sm:flex-col">
                <Plus className="h-4 w-4 text-ink-muted shrink-0 sm:mb-1" aria-hidden="true" />
                <button
                  onClick={() => toggleItem(item.product_id)}
                  className="relative group"
                  aria-label={isSelected
                    ? t('frequentlyBoughtTogether.removeItemAriaLabel', { name: item.name })
                    : t('frequentlyBoughtTogether.addItemAriaLabel', { name: item.name })}
                >
                  <div className={cn(
                    'h-24 w-24 rounded-lg overflow-hidden bg-surface-muted border-2 transition-all',
                    isSelected ? 'border-sage-500' : 'border-line opacity-50'
                  )}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BrandSymbol className="h-8 w-8 text-ink-muted" />
                      </div>
                    )}
                  </div>
                  {/* Checkbox overlay */}
                  <div className={cn(
                    'absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded border-2 transition-colors',
                    isSelected
                      ? 'border-sage-600 bg-sage-600 text-white'
                      : 'border-line-strong bg-surface'
                  )}>
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                </button>
              </div>
              <div className="sm:text-center">
                <Link
                  to={`/product/${item.slug}`}
                  className="text-sm font-medium text-ink-soft hover:text-sage-700 transition-colors line-clamp-2 sm:max-w-[96px]"
                >
                  {item.name}
                </Link>
                <p className="text-sm font-bold text-sage-700 mt-0.5">{formatCurrency(Number(item.price))}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-sm text-ink-soft">
            {t('frequentlyBoughtTogether.totalForItems', { count: selectedItems.length + 1 })}
            {discountPercent > 0 && (
              <span className="line-through text-ink-muted ml-2">{formatCurrency(combinedPrice)}</span>
            )}
            <span className="text-lg font-bold text-sage-700 ml-2">{formatCurrency(discountedTotal)}</span>
          </p>
          {discountPercent > 0 && (
            <p className="text-xs text-success-600">{t('frequentlyBoughtTogether.saveDiscount', { percent: discountPercent })}</p>
          )}
        </div>
        <Button
          onClick={handleAddAll}
          isLoading={isAdding}
          disabled={selectedItems.length === 0}
          size="sm"
        >
          <ShoppingCart className="h-4 w-4" aria-hidden="true" />
          {t('frequentlyBoughtTogether.addAllToCart')}
        </Button>
      </div>
    </section>
  );
}
